// @flow
import is from 'mz-utils/is'
import parseLatLng from 'mz-utils/parseLatLng'
import getSuggestions from './getSuggestions'
import getPlaceDetails from './getPlaceDetails'
import getNearestAddress from './getNearestAddress'
import buildSuggestionLocation from './builders/suggestionLocation'

// Types
import type { Location, SuggestionLocation } from '../../models/location'
import type { Options } from './getSuggestions'

// Constants
let RESOLVER_CACHE = {}

// Utils
export function resetResolversCache() {
  RESOLVER_CACHE = {}
}
function buildFallbackLocation(
  loc: Location,
  searchAddress: ?string
): Location {
  return {
    ...loc,
    address:
      loc.address || loc.customName || searchAddress || 'Unknown address',
  }
}
function mergeLocations(a, b): Location {
  return [...Object.keys(a), ...Object.keys(b)].reduce(
    (acc, key) => {
      let newValue = a[key]
      if (
        (newValue === '' && !is.undef(b[key])) ||
        (is.undef(newValue) && !is.undef(b[key]))
      ) {
        newValue = b[key]
      }

      acc[key] = (newValue: any)
      return acc
    },
    { ...a }
  )
}

/**
 * Try to find maximum information about given Location object.
 * Returns new Location (Promise) object with maxmimized information about
 * the place.
 * You can pass an object with only address, or only with lat/lng, and if
 * it is possible it will be resolved and filled with all available data.
 * @param  {Location} loc
 * @return {Location}
 */
function getMaximizedLocation(
  loc: Location,
  biasCoordinates?: SuggestionLocation,
  otherParams?: Options
): Promise<Location> {
  // Do nothing when all necessary data presented
  const locCoords = parseLatLng(loc.lat, loc.lng)
  if (loc.address && locCoords && loc.type) {
    return Promise.resolve(buildFallbackLocation(loc))
  }

  // Final resolver when the address is known
  const addressResolver = (searchAddress) =>
    getSuggestions(
      searchAddress,
      {
        lat: locCoords?.lat,
        lng: locCoords?.lng,
        ...biasCoordinates,
      },
      otherParams
    )
      .then((suggestions: SuggestionLocation[]) => {
        // Oh no, we didn't find anything, will have to work
        // with what we originally have
        if (!suggestions.length) {
          return buildFallbackLocation(loc, searchAddress)
        }

        // Then merge two locations to one
        const bestSuggestion =
          suggestions.find(
            (x) => x.display === searchAddress || x.value === searchAddress
          ) || suggestions[0]
        const suggestionLoc = buildSuggestionLocation(bestSuggestion)
        const betterLoc = mergeLocations(suggestionLoc, loc)

        // Reuse some important values from the old location object
        betterLoc.customName = loc.customName || betterLoc.customName

        // And if we still do not have lat/lng try to get them
        const betterCoords = parseLatLng(betterLoc.lat, betterLoc.lng)
        const finalCoords = locCoords || betterCoords

        if (finalCoords) {
          Object.assign(betterLoc, finalCoords)
          return betterLoc
        }

        if (betterLoc.placeId) {
          return getPlaceDetails(betterLoc.placeId)
            .then((detailedLoc) =>
              mergeLocations(betterLoc, buildSuggestionLocation(detailedLoc))
            )
            .catch(() => betterLoc)
        }

        return betterLoc
      })
      .catch(() => buildFallbackLocation(loc, searchAddress))

  // Try to get a cached resolver for given location
  const cachedResolver =
    RESOLVER_CACHE[loc.address] ||
    (locCoords && RESOLVER_CACHE[`${locCoords.lat}|${locCoords.lng}`])
  if (cachedResolver) {
    return cachedResolver
  }

  // If no cached resolver found, let's start the resolving process
  if (loc.address) {
    const result = addressResolver(loc.address)
    RESOLVER_CACHE[loc.address] = result

    return result.then((finalLoc) => {
      const finalCoords = parseLatLng(finalLoc.lat, finalLoc.lng)
      if (finalCoords) {
        RESOLVER_CACHE[`${finalCoords.lat}|${finalCoords.lng}`] = result
      }
      return finalLoc
    })
  }
  if (locCoords) {
    const result = getNearestAddress({ lat: locCoords.lat, lng: locCoords.lng })
      .then(addressResolver)
      .catch(() => buildFallbackLocation(loc))

    RESOLVER_CACHE[`${locCoords.lat}|${locCoords.lng}`] = result

    return result.then((finalLoc) => {
      if (finalLoc.address) {
        RESOLVER_CACHE[finalLoc.address] = result
      }
      return finalLoc
    })
  }
  return Promise.resolve(buildFallbackLocation(loc))
}

export default getMaximizedLocation
